import Konva from 'konva';
import { calculateOptimalStepSize, drawBars, drawLine, getMeasurementLabel, getSoilLayerColor } from './soil-survey-utils';
import { DASH_PATTERNS_ARRAY, GRAY, GRID_LINE_WIDTH } from './soil-survey-constants';
import { GridOptions, Measurement, RKData } from './soil-survey-types';

// Fixed RK data gathering function
export const getRKData = (measurements: Measurement[]): RKData[] => {
    const rkDataByDepth: {
        [depth: number]: { grainSizes: number[]; passingPercentages: number[] };
    } = {};

    for (const measurement of measurements) {
        if (measurement.extraData) {
            for (const extra of measurement.extraData) {
                if (extra.type === 'RK') {
                    // Handle different possible formats in RK data
                    const parts = extra.data.trim().split(/\s+/);
                    if (parts.length >= 2) {
                        const size = parseFloat(parts[0]);
                        const percent = parseFloat(parts[1]);

                        // More robust validation
                        if (!isNaN(size) && size > 0 && !isNaN(percent) && percent >= 0 && percent <= 100) {
                            if (!rkDataByDepth[measurement.depth]) {
                                rkDataByDepth[measurement.depth] = {
                                    grainSizes: [],
                                    passingPercentages: [],
                                };
                            }

                            rkDataByDepth[measurement.depth].grainSizes.push(size);
                            rkDataByDepth[measurement.depth].passingPercentages.push(percent);
                        }
                    }
                }
            }
        }
    }

    // Convert to array and sort grain sizes for each depth
    const rkData: RKData[] = [];
    for (const [depthStr, data] of Object.entries(rkDataByDepth)) {
        const depth = parseFloat(depthStr);

        // Create array of paired data for sorting
        const pairedData = data.grainSizes.map((size, index) => ({
            size,
            percent: data.passingPercentages[index],
        }));

        // Sort by grain size (ascending)
        pairedData.sort((a, b) => a.size - b.size);

        // Extract sorted arrays
        const sortedSizes = pairedData.map(item => item.size);
        const sortedPercentages = pairedData.map(item => item.percent);

        // Additional validation: ensure percentages are in descending order for grain size curves
        // (larger particles should have higher passing percentages)
        const isValidCurve = sortedPercentages.every((percent, index) => {
            if (index === 0) return true;
            return percent <= sortedPercentages[index - 1] + 5; // Allow small tolerance for measurement errors
        });

        if (isValidCurve || sortedSizes.length <= 2) {
            // Accept curves with few points even if not perfectly ordered
            rkData.push({
                depth,
                grainSizes: sortedSizes,
                passingPercentages: sortedPercentages,
            });
        } else {
            console.warn(`RK curve validation failed for depth ${depth}m - percentages may be incorrect`);
            // Still include the data but log warning
            rkData.push({
                depth,
                grainSizes: sortedSizes,
                passingPercentages: sortedPercentages,
            });
        }
    }

    return rkData;
};

export const createRKGraph = (stage: Konva.Stage, rkData: RKData, depth: number, graphOriginX: number, graphOriginY: number): Konva.Group => {
    const graphGroup = new Konva.Group({
        x: graphOriginX,
        y: graphOriginY,
    });

    const graphWidth = 400;
    const graphHeight = 300;
    const margin = 40;

    // Background
    const background = new Konva.Rect({
        x: 0,
        y: 0,
        width: graphWidth,
        height: graphHeight + 100,
        fill: 'white',
        stroke: 'black',
        strokeWidth: 2,
        shadowColor: 'black',
        shadowBlur: 10,
        shadowOpacity: 0.3,
    });
    graphGroup.add(background);

    // Title
    const title = new Konva.Text({
        x: 0,
        y: 10,
        width: graphWidth,
        text: `Rakeisuuskäyrä - Syvyys ${depth}m`,
        fontSize: 16,
        fontFamily: 'Arial',
        fill: 'black',
        align: 'center',
        fontStyle: 'bold',
    });
    graphGroup.add(title);

    // Graph plot area
    const plotWidth = graphWidth - 2 * margin;
    const plotHeight = graphHeight - 2 * margin - 30;
    const plotX = margin;
    const plotY = margin + 30;

    // Graph border
    const plotBorder = new Konva.Rect({
        x: plotX,
        y: plotY,
        width: plotWidth,
        height: plotHeight,
        stroke: 'black',
        strokeWidth: 1,
        fill: 'white',
    });
    graphGroup.add(plotBorder);

    // X-axis
    const zeroSpace = 30;
    const logPlotStart = plotX + zeroSpace;
    const logPlotWidth = plotWidth - zeroSpace;

    // Log scale range for values > 0
    const logMin = Math.log10(0.001);
    const logMax = Math.log10(600);

    // Major boundaries for soil classification
    const majorBoundaries = [0.002, 0.063, 2, 60];

    // X-axis tick values including 0
    const xAxisTickValues = [0, 0.006, 0.02, 0.2, 0.6, 6, 20, 200, 600];

    // Function to get x position for any grain size
    const getXPosition = (size: number): number => {
        if (size === 0) {
            return plotX; // 0 at the very left
        } else if (size >= 0.001) {
            const logSize = Math.log10(size);
            return logPlotStart + ((logSize - logMin) / (logMax - logMin)) * logPlotWidth;
        } else {
            return plotX + 2; // Very small values just after 0
        }
    };

    // Draw prominent black vertical lines at major boundaries
    majorBoundaries.forEach(size => {
        const x = getXPosition(size);

        const boundaryLine = new Konva.Line({
            points: [x, plotY, x, plotY + plotHeight],
            stroke: 'black',
            strokeWidth: 1.5,
        });
        graphGroup.add(boundaryLine);

        // Add mm value label on the x-axis for the boundary
        let sizeLabelText: string;
        if (size < 0.01) {
            sizeLabelText = size.toFixed(3);
        } else if (size < 1) {
            sizeLabelText = size.toFixed(2);
        } else {
            sizeLabelText = size.toString();
        }

        const boundaryLabel = new Konva.Text({
            x: x,
            y: plotY + plotHeight + 5,
            text: sizeLabelText,
            fontSize: 8,
            fontFamily: 'Arial',
            fill: 'black',
            align: 'center',
            fontStyle: 'bold',
        });
        boundaryLabel.offsetX(boundaryLabel.width() / 2);
        graphGroup.add(boundaryLabel);
    });

    // Draw vertical grid lines and x-axis labels
    xAxisTickValues.forEach(size => {
        const x = getXPosition(size);

        // Grid line
        const gridLine = new Konva.Line({
            points: [x, plotY, x, plotY + plotHeight],
            stroke: GRAY,
            strokeWidth: GRID_LINE_WIDTH,
        });
        graphGroup.add(gridLine);

        // X-axis label
        let sizeLabelText: string;
        if (size === 0) {
            sizeLabelText = '0';
        } else if (size < 0.01) {
            sizeLabelText = size.toFixed(3);
        } else if (size < 1) {
            sizeLabelText = size.toFixed(2);
        } else {
            sizeLabelText = size.toString();
        }

        const sizeLabel = new Konva.Text({
            x: x,
            y: plotY + plotHeight + 5,
            text: sizeLabelText,
            fontSize: 7,
            fontFamily: 'Arial',
            fill: 'black',
            align: 'center',
        });
        sizeLabel.offsetX(sizeLabel.width() / 2);
        graphGroup.add(sizeLabel);
    });

    // Soil type labels
    const soilTypeBoundaries = [0, 0.002, 0.063, 2, 60, 600];
    const soilTypeLabels = ['savi', 'siltti', 'hiekka', 'sora', 'kivet', 'lohkareet'];

    for (let i = 0; i < soilTypeLabels.length; i++) {
        const startBoundary = soilTypeBoundaries[i];
        const x = getXPosition(startBoundary);

        const soilLabel = new Konva.Text({
            x: x,
            y: plotY + plotHeight + 35,
            text: soilTypeLabels[i],
            fontSize: 7,
            fontFamily: 'Arial',
            fill: 'black',
            rotation: 45,
            align: 'center',
        });
        soilLabel.offsetX(soilLabel.width() / 2);
        soilLabel.offsetY(soilLabel.height() / 2);
        graphGroup.add(soilLabel);
    }

    // Draw horizontal grid lines and y-axis labels (0-100%)
    for (let i = 0; i <= 10; i++) {
        const percent = i * 10;
        const y = plotY + plotHeight - (percent / 100) * plotHeight;

        // Grid line
        const gridLine = new Konva.Line({
            points: [plotX, y, plotX + plotWidth, y],
            stroke: GRAY,
            strokeWidth: GRID_LINE_WIDTH,
        });
        graphGroup.add(gridLine);

        // Y-axis label
        const label = new Konva.Text({
            x: plotX - 25,
            y: y - 5,
            text: percent.toString(),
            fontSize: 8,
            fontFamily: 'Arial',
            fill: 'black',
        });
        graphGroup.add(label);
    }

    // Axis labels
    const xLabel = new Konva.Text({
        x: plotX + plotWidth / 2,
        y: plotY + plotHeight + 90,
        text: 'Rakeiden läpimitta (mm)',
        fontSize: 10,
        fontFamily: 'Arial',
        fill: 'black',
        fontStyle: 'bold',
        align: 'center',
    });
    xLabel.offsetX(xLabel.width() / 2);
    graphGroup.add(xLabel);

    const yLabel = new Konva.Text({
        x: 5,
        y: plotY + plotHeight / 2,
        text: 'Läpäisyprosentti (%)',
        fontSize: 10,
        fontFamily: 'Arial',
        fill: 'black',
        fontStyle: 'bold',
        rotation: -90,
        align: 'center',
    });
    yLabel.offsetX(yLabel.width() / 2);
    graphGroup.add(yLabel);

    // Draw the grain size lines
    if (rkData.grainSizes.length > 0) {
        const points: number[] = [];
        const validPoints: { x: number; y: number }[] = [];

        validPoints.push({ x: plotX, y: plotY + plotHeight });

        // Process all data points
        for (let i = 0; i < rkData.grainSizes.length; i++) {
            const size = rkData.grainSizes[i];
            const percent = rkData.passingPercentages[i];

            if (size >= 0 && size <= 600 && percent >= 0 && percent <= 100) {
                const x = getXPosition(size);
                const y = plotY + plotHeight - (percent / 100) * plotHeight;

                // Clamp to plot area
                const clampedX = Math.max(plotX, Math.min(x, plotX + plotWidth));
                const clampedY = Math.max(plotY, Math.min(y, plotY + plotHeight));

                validPoints.push({ x: clampedX, y: clampedY });
            }
        }

        // Sort points by x-coordinate
        validPoints.sort((a, b) => a.x - b.x);

        // Create points array for line drawing
        validPoints.forEach(point => {
            points.push(point.x, point.y);
        });

        // Draw data points (circles) - skip the artificial (0,0) point
        for (let i = 1; i < validPoints.length; i++) {
            const point = validPoints[i];
            const circle = new Konva.Circle({
                x: point.x,
                y: point.y,
                radius: 3,
                fill: 'blue',
                stroke: 'darkblue',
                strokeWidth: 1,
            });
            graphGroup.add(circle);
        }

        // Draw the connecting line
        if (points.length >= 4) {
            const line = new Konva.Line({
                points: points,
                stroke: 'blue',
                strokeWidth: 2,
                lineCap: 'round',
                lineJoin: 'round',
            });
            graphGroup.add(line);
        }
    }

    return graphGroup;
};

export const drawRKIcons = (layer: Konva.Layer, stage: Konva.Stage, measurements: Measurement[], startY: number, tt: string) => {
    const rkData = getRKData(measurements);
    if (rkData.length === 0) return;

    const { barX, barY, barWidth, maxDepth, barHeight } = calculateValues(tt, stage, startY, measurements);
    const adjustedBarY = barY - 20;

    // Variable to track the currently displayed graph and its hide timeout
    let currentGraph: Konva.Group | null = null;
    let hideGraphTimeout: ReturnType<typeof setTimeout> | null = null;

    // Function to hide the graph
    const hideGraph = () => {
        if (hideGraphTimeout) {
            clearTimeout(hideGraphTimeout);
            hideGraphTimeout = null;
        }
        stage.container().style.cursor = 'default';
        if (currentGraph) {
            currentGraph.destroy();
            currentGraph = null;
            layer.batchDraw();
        }
    };

    for (const rk of rkData) {
        const depth = rk.depth;
        const y = adjustedBarY + (depth / maxDepth) * barHeight;

        // Constants for icon size and positioning
        const iconWidth = 28;
        const iconHeight = 18;
        const iconX = barX + barWidth / 2 - iconWidth / 2;

        // Create RK icon background
        const iconBg = new Konva.Rect({
            x: iconX,
            y: y - iconHeight / 2,
            width: iconWidth,
            height: iconHeight,
            fill: 'orange',
            stroke: 'yellow',
            strokeWidth: 1,
            cornerRadius: 3,
        });

        const iconText = new Konva.Text({
            x: iconX,
            y: iconBg.y() + iconBg.height() / 2 - 5,
            width: iconWidth,
            text: 'RK',
            fontSize: 10,
            fontFamily: 'Arial',
            fill: 'darkblue',
            fontStyle: 'bold',
            align: 'center',
        });

        // Add hover effects and show graph on hover
        const iconGroup = new Konva.Group();
        iconGroup.add(iconBg);
        iconGroup.add(iconText);

        iconGroup.on('mouseenter', () => {
            // Clear any pending hide timeout if mouse re-enters quickly
            if (hideGraphTimeout) {
                clearTimeout(hideGraphTimeout);
                hideGraphTimeout = null;
            }

            iconBg.fill('yellow');
            stage.container().style.cursor = 'pointer';

            // Remove any existing graph before creating a new one
            if (currentGraph) {
                currentGraph.destroy();
                currentGraph = null;
            }

            // Get current mouse position relative to the stage
            const pointerPos = stage.getPointerPosition();
            if (!pointerPos) return; // Should not happen on mouseenter

            // Calculate position for the graph: offset to the right and slightly down from the mouse
            const offsetX = 15; // Offset to the right of the cursor
            const offsetY = 15; // Offset below the cursor
            const graphDesiredWidth = 400;
            const graphDesiredHeight = 300 + 100;

            let finalGraphX = pointerPos.x + offsetX;
            let finalGraphY = pointerPos.y + offsetY;

            // Adjust graph position to stay within stage boundaries
            if (finalGraphX + graphDesiredWidth > stage.width()) {
                finalGraphX = stage.width() - graphDesiredWidth - 10; // 10px margin from right edge
                // Ensure it doesn't go off left edge if icon is too far right
                if (finalGraphX < 0) finalGraphX = 10; // 10px margin from left edge
            }

            if (finalGraphY + graphDesiredHeight > stage.height()) {
                finalGraphY = stage.height() - graphDesiredHeight - 10; // 10px margin from bottom edge
                // Ensure it doesn't go off top edge if icon is too far down
                if (finalGraphY < 0) finalGraphY = 10; // 10px margin from top edge
            }

            // Create and show new graph, passing the calculated position
            // Pass originX and originY to createRKGraph
            currentGraph = createRKGraph(stage, rk, depth, finalGraphX, finalGraphY);
            currentGraph.name('rk-graph-hover');
            layer.add(currentGraph);
            currentGraph.moveToTop(); // Ensure the graph is on top of other elements
            layer.batchDraw();

            currentGraph.on('mouseleave', () => {
                hideGraph(); // Call the unified hide function
                iconBg.fill('orange'); // Revert icon color when graph hides
            });

            // If mouse re-enters graph, clear icon's hide timeout
            // This prevents the icon's mouseleave from hiding the graph if the mouse just transitioned.
            currentGraph.on('mouseenter', () => {
                if (hideGraphTimeout) {
                    clearTimeout(hideGraphTimeout);
                    hideGraphTimeout = null;
                }
            });
        });

        iconGroup.on('mouseleave', () => {
            // Start a short delay before hiding the graph.
            // This allows the mouse to transition from the icon to the graph without hiding it immediately.
            hideGraphTimeout = setTimeout(() => {
                // Before hiding, check if the mouse is currently over the graph.
                // This prevents hiding if the mouse has moved from the icon TO the graph.
                const pointerPos = stage.getPointerPosition();
                const graphAbsPos = currentGraph ? currentGraph.getAbsolutePosition() : null;
                const graphWidth = 400;
                const graphHeight = 300 + 100;

                if (
                    currentGraph &&
                    pointerPos &&
                    graphAbsPos &&
                    pointerPos.x >= graphAbsPos.x &&
                    pointerPos.x <= graphAbsPos.x + graphWidth &&
                    pointerPos.y >= graphAbsPos.y &&
                    pointerPos.y <= graphAbsPos.y + graphHeight
                ) {
                    // Mouse is now over the graph, do not hide.
                    // The graph's own mouseleave will handle hiding when cursor leaves the graph.
                    // Ensure the timeout is cleared so icon's mouseleave doesn't interfere later.
                    hideGraphTimeout = null;
                    return;
                }

                // If mouse is not over the graph, then hide it.
                iconBg.fill('orange'); // Revert to original fill color
                hideGraph(); // Call the unified hide function
            }, 100);
        });

        layer.add(iconGroup);
    }
};

export const drawGridLines = (options: GridOptions) => {
    const { layer, barX, barY, barWidth, barHeight, maxDepth, stepSize, maxLeftLength = 0, maxRightLength = 0, drawLeft = false, drawRight = false } = options;

    // Account for the -20 offset used in drawDepthBar
    const adjustedBarY = barY - 20;

    // Draw horizontal grid lines
    for (let i = 0; i <= maxDepth; i++) {
        if (i % 1 === 0) {
            const y = adjustedBarY + (i / maxDepth) * barHeight;

            // Left side horizontal lines
            if (drawLeft && maxLeftLength > 0) {
                const leftGridLine = new Konva.Line({
                    points: [barX - maxLeftLength * stepSize, y, barX, y],
                    stroke: GRAY,
                    strokeWidth: GRID_LINE_WIDTH,
                });
                layer.add(leftGridLine);
            }

            // Right side horizontal lines
            if (drawRight && maxRightLength > 0) {
                const rightGridLine = new Konva.Line({
                    points: [barX + barWidth, y, barX + barWidth + maxRightLength * stepSize, y],
                    stroke: GRAY,
                    strokeWidth: GRID_LINE_WIDTH,
                });
                layer.add(rightGridLine);
            }
        }
    }

    // Draw vertical grid lines for left side
    if (drawLeft && maxLeftLength > 0) {
        for (let i = 0; i <= maxLeftLength; i++) {
            const x = barX - i * stepSize;
            const verticalLine = new Konva.Line({
                points: [x, adjustedBarY, x, adjustedBarY + barHeight],
                stroke: GRAY,
                strokeWidth: GRID_LINE_WIDTH,
            });
            layer.add(verticalLine);
        }
    }

    // Draw vertical grid lines for right side
    if (drawRight && maxRightLength > 0) {
        for (let i = 0; i <= maxRightLength; i++) {
            const x = barX + barWidth + i * stepSize;
            const verticalLine = new Konva.Line({
                points: [x, adjustedBarY, x, adjustedBarY + barHeight],
                stroke: GRAY,
                strokeWidth: GRID_LINE_WIDTH,
            });
            layer.add(verticalLine);
        }
    }
};

export const getNONEValues = (measurements: Measurement[]) => {
    const lbData: { [key: string]: { depth: number; value: number }[] } = {};
    const uniqueParams: string[] = [];

    // First pass: collect all LB data and identify unique parameters
    for (const measurement of measurements) {
        if (measurement.extraData) {
            for (const extra of measurement.extraData) {
                if (extra.type === 'LB') {
                    const parts = extra.data.split(/\s+/);
                    const param = parts[0]; // "w", "F", etc.
                    const value = parseFloat(parts[1]); // 23.70, 55.70, etc.

                    if (!isNaN(value)) {
                        // Track unique parameters in order of first appearance
                        if (!uniqueParams.includes(param)) {
                            uniqueParams.push(param);
                        }

                        // Group by parameter
                        if (!lbData[param]) {
                            lbData[param] = [];
                        }

                        lbData[param].push({
                            depth: measurement.depth,
                            value: value,
                        });
                    }
                }
            }
        }
    }

    // Calculate max value for each parameter and sort by max value
    const paramMaxValues = uniqueParams.map(param => {
        const sortedData = lbData[param].sort((a, b) => a.depth - b.depth);
        const maxValue = Math.max(...sortedData.map(item => item.value));
        return { param, maxValue, sortedData };
    });

    // Sort parameters by max value (descending - highest first)
    paramMaxValues.sort((a, b) => b.maxValue - a.maxValue);

    // Split parameters: highest max values go to left, lowest go to right
    const left: { [param: string]: { depth: number; value: number }[] } = {};
    const right: { [param: string]: { depth: number; value: number }[] } = {};

    const halfPoint = Math.ceil(paramMaxValues.length / 2);

    paramMaxValues.forEach((item, index) => {
        if (index < halfPoint) {
            // First half (highest max values) -> left side
            left[item.param] = item.sortedData;
        } else {
            // Second half (lowest max values) -> right side
            right[item.param] = item.sortedData;
        }
    });

    // Find max values across all parameters on each side
    const leftValues = Object.values(left)
        .flat()
        .map(item => item.value);
    const rightValues = Object.values(right)
        .flat()
        .map(item => item.value);

    const maxLeft = leftValues.length > 0 ? Math.max(...leftValues) : 0;
    const maxRight = rightValues.length > 0 ? Math.max(...rightValues) : 0;

    return {
        data: { left, right },
        maxLeft,
        maxRight,
    };
};

export const calculateValues = (tt: string, stage: Konva.Stage, startY: number, measurements: Measurement[] = []) => {
    let maxLeftLength = 0;
    let maxRightLength = 0;
    let rightStepSize = 10;
    let leftStepSize = 10;

    switch (tt) {
        case 'PA': {
            const rawMaxRight = Math.max(...measurements.map(m => m.value2));
            const rawMaxLeft = Math.max(...measurements.map(m => (m.value1 * 9.81) / 1000));

            const rightOptimal = calculateOptimalStepSize(rawMaxRight);
            rightStepSize = rightOptimal.stepSize;
            maxRightLength = rightOptimal.stepCount;

            // For left side, keep simpler logic for now
            maxLeftLength = rawMaxLeft < 10 ? Math.ceil(rawMaxLeft) : Math.ceil(rawMaxLeft / 10) * 10;
            maxLeftLength *= 2;
            leftStepSize = 1; // Left side uses different scaling
            break;
        }

        case 'SI': {
            const maxParameter1 = Math.max(...measurements.map(m => m.value1));
            const maxParameter2 = Math.max(...measurements.map(m => m.value2));
            const maxValue = Math.max(maxParameter1, maxParameter2);

            const optimal = calculateOptimalStepSize(maxValue);
            rightStepSize = optimal.stepSize;
            maxRightLength = optimal.stepCount;
            maxLeftLength = 0;
            break;
        }

        case 'PO': {
            const maxParameter1 = Math.max(...measurements.map(m => m.value1));

            const optimal = calculateOptimalStepSize(maxParameter1);
            leftStepSize = optimal.stepSize;
            maxLeftLength = optimal.stepCount;
            maxRightLength = 0;
            break;
        }

        case 'TR':
            maxLeftLength = 0;
            maxRightLength = 0;
            break;

        case 'NE':
        case 'NO': {
            const { maxLeft, maxRight } = getNONEValues(measurements);

            if (maxLeft > 0) {
                const leftOptimal = calculateOptimalStepSize(maxLeft);
                leftStepSize = leftOptimal.stepSize;
                maxLeftLength = leftOptimal.stepCount;
            }

            if (maxRight > 0) {
                const rightOptimal = calculateOptimalStepSize(maxRight);
                rightStepSize = rightOptimal.stepSize;
                maxRightLength = rightOptimal.stepCount;
            }
            break;
        }

        case 'CU':
        case 'CPTU': {
            const maxParameter1 = Math.max(...measurements.map(m => m.value1));
            const maxParameter2 = Math.max(...measurements.map(m => m.value2));
            const maxParameter3 = Math.max(...measurements.map(m => m.value3));
            const maxParameter4 = Math.max(...measurements.map(m => m.value4));
            const maxValueRight = Math.max(maxParameter1, maxParameter2, maxParameter3);
            const optimalRight = calculateOptimalStepSize(maxValueRight);
            const optimalLeft = calculateOptimalStepSize(maxParameter4);
            rightStepSize = optimalRight.stepSize;
            maxRightLength = optimalRight.stepCount;
            maxLeftLength = optimalLeft.stepCount;
            leftStepSize = optimalLeft.stepSize;
            break;
        }

        case 'HP': {
            const maxParameter1 = Math.max(...measurements.map(m => m.value1));
            const maxParameter2 = Math.max(...measurements.map(m => m.value2));
            const optimalRight = calculateOptimalStepSize(maxParameter1);
            const optimalLeft = calculateOptimalStepSize(maxParameter2);
            rightStepSize = optimalRight.stepSize;
            maxRightLength = optimalRight.stepCount;
            maxLeftLength = optimalLeft.stepCount;
            leftStepSize = optimalLeft.stepSize;
            break;
        }

        case 'HE': {
            const maxParameter = Math.max(...measurements.map(m => m.value1));
            const optimalRight = calculateOptimalStepSize(maxParameter);
            rightStepSize = optimalRight.stepSize;
            maxRightLength = optimalRight.stepCount;
            break;
        }

        default:
            break;
    }

    // Calculate bar positioning values
    const maxDepth = Math.max(...measurements.map(m => m.depth));
    const barHeight = 400;
    const max = Math.max(...measurements.map(m => m.value2));
    const margin = 10;
    const barWidth = 60;

    // Calculate dynamic stepSize based on available space
    const sideMargin = 50; // Leave margin on each side
    const availableWidth = stage.width() - 2 * sideMargin;
    const totalGraphSteps = maxLeftLength + maxRightLength;

    let stepSize = 60;
    if (totalGraphSteps > 0) {
        const maxPossibleStepSize = (availableWidth - barWidth) / totalGraphSteps;
        stepSize = Math.min(60, Math.max(20, maxPossibleStepSize));
    }

    // Calculate total width needed for the entire graph system
    const leftGraphSpace = maxLeftLength * stepSize;
    const rightGraphSpace = maxRightLength * stepSize;
    const totalNeededWidth = leftGraphSpace + barWidth + rightGraphSpace;

    // Center the entire graph system
    const startX = (stage.width() - totalNeededWidth) / 2;
    const barX = startX + leftGraphSpace;

    const barY = startY + 50;
    const roundedMax = Math.ceil(max / rightStepSize) * rightStepSize;

    // Calculate tick mark positions
    const tickMarkPosition1 = tt === 'PA' ? barX - 5 : barX + barWidth + 5;
    const tickMarkPosition2 = tt === 'PA' ? barX : barX + barWidth;

    // Calculate end line positions
    const endLinePosition1 = tt === 'PA' ? barX + barWidth : barX - margin / 2;
    const endLinePosition2 = tt === 'PA' ? barX + barWidth + (roundedMax / rightStepSize) * stepSize : barX + barWidth + margin / 2;

    return {
        maxLeftLength,
        maxRightLength,
        tickMarkPosition1,
        tickMarkPosition2,
        barWidth,
        barX,
        margin,
        barY,
        maxDepth,
        barHeight,
        endLinePosition1,
        endLinePosition2,
        stepSize,
        rightStepSize,
        leftStepSize,
    };
};

export const drawLabels = (
    layer: Konva.Layer,
    tt: string,
    barX: number,
    barY: number,
    barHeight: number,
    barWidth: number,
    margin: number,
    stepSize: number,
    maxRightLength: number,
    maxLeftLength: number,
    maxDepth: number,
    measurements: Measurement[]
) => {
    const createRightAlignedText = (text: string, y: number, rightEdge: number, color = 'black') => {
        const tempText = new Konva.Text({
            text: text,
            fontSize: 10,
            fontFamily: 'Arial',
        });
        const textWidth = tempText.width();

        return new Konva.Text({
            x: rightEdge - textWidth,
            y: y,
            text: text,
            fontSize: 10,
            fontFamily: 'Arial',
            fill: color,
        });
    };

    const createLeftAlignedText = (text: string, y: number, leftEdge: number, color = 'black') => {
        return new Konva.Text({
            x: leftEdge,
            y: y,
            text: text,
            fontSize: 10,
            fontFamily: 'Arial',
            fill: color,
        });
    };

    switch (tt) {
        case 'PA': {
            if (maxRightLength > 0) {
                const rightEdge = barX + barWidth + maxRightLength * stepSize;
                layer.add(createRightAlignedText('Puolikierrokset', barY + barHeight + margin * 2, rightEdge));
            }

            const leftEdge = barX - maxLeftLength * stepSize - margin * 2 - 8;
            layer.add(createLeftAlignedText('kN', barY + barHeight + margin / 2, leftEdge));

            const leftEdgeVapaa = barX - maxLeftLength * stepSize - margin * 2 - 2;
            layer.add(createLeftAlignedText('Vapaapainuma', barY + barHeight + margin * 2, leftEdgeVapaa));
            break;
        }
        case 'SI': {
            const rightEdge = barX + barWidth + maxRightLength * stepSize - 2;

            const lineConfigs = [
                {
                    label: 'kN/m2 Leikkauslujuus',
                    dashPattern: null as number[] | null,
                    hasValue: measurements.some(m => m.value1 !== undefined && m.value1 !== null && !Number.isNaN(m.value1)),
                },
                {
                    label: 'kN/m2 Häiritty leikkauslujuus',
                    dashPattern: [5, 5],
                    hasValue: measurements.some(m => m.value2 !== undefined && m.value2 !== null && !Number.isNaN(m.value2)),
                },
                {
                    label: 'Sensitiivisyys',
                    dashPattern: [2, 2],
                    hasValue: measurements.some(m => m.value3 !== undefined && m.value3 !== null && !Number.isNaN(m.value3)),
                },
                {
                    label: 'MPa Jäännöslujuus',
                    dashPattern: [8, 3, 2, 3],
                    hasValue: measurements.some(m => m.value4 !== undefined && m.value4 !== null && !Number.isNaN(m.value4)),
                },
            ];

            let labelIndex = 2;
            lineConfigs.forEach(config => {
                if (config.hasValue) {
                    const labelY = barY + barHeight + margin * labelIndex;

                    layer.add(createRightAlignedText(config.label, labelY, rightEdge));

                    const lineLength = 30;
                    const lineX = rightEdge + 5;
                    const lineY = labelY + 5;

                    const exampleLine = new Konva.Line({
                        points: [lineX, lineY, lineX + lineLength, lineY],
                        stroke: 'black',
                        strokeWidth: 1.5,
                        dash: config.dashPattern ? [...config.dashPattern] : undefined,
                    });
                    layer.add(exampleLine);

                    labelIndex++;
                }
            });
            break;
        }
        case 'PO': {
            if (maxLeftLength > 0) {
                const leftEdgeKairaus = barX - maxLeftLength * stepSize - margin * 2 - 2;
                layer.add(createLeftAlignedText('Kairausvastus (s)', barY + barHeight + margin * 2, leftEdgeKairaus));
            }
            break;
        }
        case 'TR':
            // Empty case - no labels to draw
            break;

        case 'NE':
        case 'NO': {
            // Get the parameter names from NONE values
            const { data } = getNONEValues(measurements);

            // Draw left side parameter labels with example lines
            if (maxLeftLength > 0 && Object.keys(data.left).length > 0) {
                const leftEdgeNE = barX - maxLeftLength * stepSize - margin + 2;
                const leftParamNames = Object.keys(data.left);

                leftParamNames.forEach((param, paramIndex) => {
                    const labelY = barY + barHeight + margin * (2 + paramIndex);

                    // Add the text label
                    layer.add(createLeftAlignedText(getMeasurementLabel(param), labelY, leftEdgeNE));

                    // Add example line to the right of the label
                    const lineLength = 30;
                    const lineX = leftEdgeNE - lineLength - 10; // Position to the left of the label
                    const lineY = labelY + 5; // Center vertically with text

                    // Get dash pattern based on parameter index (same logic as in drawNONEValues)
                    const dashIndex = paramIndex % DASH_PATTERNS_ARRAY.length;
                    const dashPattern = DASH_PATTERNS_ARRAY[dashIndex];

                    const exampleLine = new Konva.Line({
                        points: [lineX, lineY, lineX + lineLength, lineY],
                        stroke: 'black',
                        strokeWidth: 1.5,
                        dash: dashPattern ? [...dashPattern] : undefined,
                    });
                    layer.add(exampleLine);
                });
            }

            // Draw right side parameter labels with example lines
            if (maxRightLength > 0 && Object.keys(data.right).length > 0) {
                const rightEdgeNE = barX + barWidth + maxRightLength * stepSize - 2;
                const rightParamNames = Object.keys(data.right);

                rightParamNames.forEach((param, paramIndex) => {
                    const labelY = barY + barHeight + margin * (2 + paramIndex);

                    // Add the text label
                    layer.add(createRightAlignedText(getMeasurementLabel(param), labelY, rightEdgeNE));

                    // Add example line to the right of the label
                    const lineLength = 30;
                    const lineX = rightEdgeNE + 5; // Start 5px to the right of the right edge
                    const lineY = labelY + 5; // Center vertically with text

                    // Get dash pattern based on parameter index (same logic as in drawNONEValues)
                    const dashIndex = paramIndex % DASH_PATTERNS_ARRAY.length;
                    const dashPattern = DASH_PATTERNS_ARRAY[dashIndex];

                    const exampleLine = new Konva.Line({
                        points: [lineX, lineY, lineX + lineLength, lineY],
                        stroke: 'black',
                        strokeWidth: 1.5,
                        dash: dashPattern ? [...dashPattern] : undefined,
                    });
                    layer.add(exampleLine);
                });
            }
            break;
        }

        case 'CU':
        case 'CPTU': {
            if (maxLeftLength > 0) {
                const leftEdgeCU = barX - maxLeftLength * stepSize - margin * 2 - 8;
                layer.add(createLeftAlignedText('Huokospaine', barY + barHeight + margin * 2, leftEdgeCU));
                layer.add(createLeftAlignedText('(kN/m²)', barY + barHeight + margin * 3, leftEdgeCU));
            }

            if (maxRightLength > 0) {
                const rightEdgeCU = barX + barWidth + maxRightLength * stepSize - 2;

                const lineConfigs = [
                    {
                        label: 'Kok.vastus (MN/m²)',
                        dashPattern: null as number[] | null,
                        hasValue: measurements.some(m => m.value1 !== undefined && m.value1 !== null && !Number.isNaN(m.value1)),
                    },
                    {
                        label: 'Vaippavastus (kN/m²)',
                        dashPattern: [5, 5],
                        hasValue: measurements.some(m => m.value2 !== undefined && m.value2 !== null && !Number.isNaN(m.value2)),
                    },
                    {
                        label: 'Kärkivastus (MN/m²)',
                        dashPattern: [2, 2],
                        hasValue: measurements.some(m => m.value3 !== undefined && m.value3 !== null && !Number.isNaN(m.value3)),
                    },
                ];

                let labelIndex = 2;
                lineConfigs.forEach(config => {
                    if (config.hasValue) {
                        const labelY = barY + barHeight + margin * labelIndex;

                        layer.add(createRightAlignedText(config.label, labelY, rightEdgeCU));

                        const lineLength = 30;
                        const lineX = rightEdgeCU + 5;
                        const lineY = labelY + 5;

                        const exampleLine = new Konva.Line({
                            points: [lineX, lineY, lineX + lineLength, lineY],
                            stroke: 'black',
                            strokeWidth: 1.5,
                            dash: config.dashPattern ? [...config.dashPattern] : undefined,
                        });
                        layer.add(exampleLine);

                        labelIndex++;
                    }
                });
            }
            break;
        }
        case 'HP':
            if (maxLeftLength > 0) {
                const leftEdgeHP = barX - maxLeftLength * stepSize - margin * 2 - 8;
                layer.add(createLeftAlignedText('Vääntömomentti', barY + barHeight + margin * 2, leftEdgeHP));
                layer.add(createLeftAlignedText('(Nm/100)', barY + barHeight + margin * 3, leftEdgeHP));
            }
            if (maxRightLength > 0) {
                const rightEdgeHP = barX + barWidth + maxRightLength * stepSize - 2;
                layer.add(createRightAlignedText('Lyönnit / Puristuspaine (MN/m²)', barY + barHeight + margin * 2, rightEdgeHP));

                const hasHType = measurements.some(m => !m.extraData || m.extraData.length === 0 || m.extraData.some(e => e.type === 'HP' && e.data === 'H'));
                const hasPType = measurements.some(m => m.extraData && m.extraData.some(e => e.type === 'HP' && e.data === 'P'));

                if (hasHType && hasPType) {
                    layer.add(createRightAlignedText('H: portaat, P: viiva', barY + barHeight + margin * 4, rightEdgeHP));
                }
            }
            break;
        case 'HE':
            if (maxRightLength > 0) {
                const rightEdgeHE = barX + barWidth + maxRightLength * stepSize - 2;
                layer.add(createRightAlignedText('Lyönnit', barY + barHeight + margin * 2, rightEdgeHE));
            }
            break;
        default:
            break;
    }
};

export const drawGraphGrid = (layer: Konva.Layer, stage: Konva.Stage, measurements: Measurement[], startY: number, tt: string) => {
    const { maxLeftLength, maxRightLength, barX, barY, barHeight, margin, barWidth, stepSize, maxDepth, rightStepSize, leftStepSize } = calculateValues(
        tt,
        stage,
        startY,
        measurements
    );

    if (maxLeftLength > 0) {
        const line = new Konva.Line({
            points: [barX, barY + barHeight, barX - maxLeftLength * stepSize, barY + barHeight],
            stroke: 'black',
            strokeWidth: 1.5,
        });
        layer.add(line);
    }
    for (let i = 1; i <= maxLeftLength; i++) {
        const line = new Konva.Line({
            points: [barX - i * stepSize, barY + barHeight - margin, barX - i * stepSize, barY + barHeight],
            stroke: 'black',
            strokeWidth: 1.5,
        });

        layer.add(line);
        const label = new Konva.Text({
            x: barX - i * stepSize, // Set x to the tick position
            y: barY + barHeight + margin / 2,
            text: (i * leftStepSize).toString(),
            fontSize: 10,
            fontFamily: 'Arial',
            fill: 'black',
            align: 'center',
        });
        label.offsetX(label.width() / 2); // Center label on tick
        layer.add(label);
    }
    const line2 = new Konva.Line({
        points: [barX + barWidth, barY + barHeight, barX + barWidth + maxRightLength * stepSize, barY + barHeight],
        stroke: 'black',
        strokeWidth: 1.5,
    });
    layer.add(line2);
    for (let i = 1; i <= maxRightLength; i++) {
        const line = new Konva.Line({
            points: [barX + barWidth + i * stepSize, barY + barHeight - margin, barX + barWidth + i * stepSize, barY + barHeight],
            stroke: 'black',
            strokeWidth: 1.5,
        });
        layer.add(line);

        const label = new Konva.Text({
            x: barX + barWidth + i * stepSize, // Set x to the tick position
            y: barY + barHeight + margin / 2,
            text: (i * rightStepSize).toString(),
            fontSize: 10,
            fontFamily: 'Arial',
            fill: 'black',
            align: 'center',
        });
        label.offsetX(label.width() / 2); // Center label on tick
        layer.add(label);
    }
    drawLabels(layer, tt, barX, barY, barHeight, barWidth, margin, stepSize, maxRightLength, maxLeftLength, maxDepth, measurements);
};

export const calculateDepthLabelStep = (maxDepth: number): number => {
    const targetLabelCount = 5;
    const rawStep = maxDepth / targetLabelCount;

    const niceSteps = [0.5, 1, 2, 5, 10, 20, 25, 50, 100, 200, 500];

    let bestStep = niceSteps[0];
    let minDiff = Math.abs(rawStep - bestStep);

    for (const step of niceSteps) {
        const diff = Math.abs(rawStep - step);
        if (diff < minDiff) {
            bestStep = step;
            minDiff = diff;
        }
    }

    return bestStep;
};

const drawAlBar = (layer: Konva.Layer, barX: number, barY: number, barWidth: number, barHeight: number, alType: string) => {
    if (alType === '-' || alType === '' || alType === 'SI' || alType === 'Lk') {
        const layer_al2 = new Konva.Rect({
            x: barX - 6,
            y: barY,
            width: barWidth + 12,
            height: barHeight,
            stroke: 'black',
            strokeWidth: 2,
            fill: 'white',
        });
        layer.add(layer_al2);

        const layer_al_1 = new Konva.Rect({
            x: barX,
            y: barY,
            width: barWidth,
            height: barHeight,
            stroke: 'black',
            strokeWidth: 2,
            fill: 'white',
        });
        layer.add(layer_al_1);
    } else if (alType === 'Ly') {
        const background = new Konva.Rect({
            x: barX - 4,
            y: barY,
            width: barWidth + 8,
            height: barHeight,
            fill: 'white',
            stroke: 'white',
        });
        layer.add(background);

        const centerX = barX + barWidth / 2;
        const layer_al = new Konva.Line({
            points: [centerX, barY, centerX, barY + barHeight],
            stroke: 'black',
            strokeWidth: 2,
        });
        layer.add(layer_al);
    }
};

export const drawDepthBar = (
    layer: Konva.Layer,
    stage: Konva.Stage,
    measurements: Measurement[],
    startY: number,
    surface: string,
    tt: string,
    alData: { alValue: number; alSoilType: string },
    currentSoil: { value: string },
    alType: string
) => {
    const { tickMarkPosition1, tickMarkPosition2, barWidth, barX, margin, barY, maxDepth, barHeight, endLinePosition1, endLinePosition2, maxRightLength, stepSize } =
        calculateValues(tt, stage, startY, measurements);
    const depthLabelStep = calculateDepthLabelStep(maxDepth);
    const alDepthOffset = alData.alValue ? (alData.alValue / maxDepth) * barHeight : 0;
    if (alData.alValue) {
        const soilType = measurements[0].soilType || '';
        const alStartY = barY + (0 / maxDepth) * barHeight - 20;
        const alEndY = barY + (alData.alValue / maxDepth) * barHeight - 20;
        const alHeight = alEndY - alStartY;
        if (soilType !== '') {
            currentSoil.value = getSoilLayerColor(soilType);
        }
        drawAlBar(layer, barX, alStartY, barWidth, alHeight, alType);
    }

    if (tt === 'TR') {
        const newX = barX + barWidth / 2;
        const lineBar = new Konva.Line({
            points: [newX, barY - 20, newX, barY + barHeight + 20],
            stroke: 'black',
            strokeWidth: 2.5,
        });
        layer.add(lineBar);
    } else {
        // Draw main depth bar border
        const barBorder = new Konva.Rect({
            x: barX,
            y: barY - 20 + alDepthOffset,
            width: barWidth,
            height: barHeight + 20 - alDepthOffset,
            stroke: 'black',
            strokeWidth: 2,
        });
        layer.add(barBorder);
    }

    // Draw depth scale on the left (or right)
    for (let i = 0; i <= maxDepth; i += depthLabelStep) {
        let offset = 0;
        if (tt === 'TR') {
            offset = barWidth / 2;
        }
        const y = barY + (i / maxDepth) * barHeight - 20;
        if (i !== 0) {
            const tick = new Konva.Line({
                points: [tickMarkPosition1 - offset, y, tickMarkPosition2 - offset, y],
                stroke: 'black',
                strokeWidth: 1,
            });
            layer.add(tick);
        } else {
            const line = new Konva.Line({
                points: [barX - margin / 2, y, barX + barWidth + margin / 2, y],
                stroke: 'black',
                strokeWidth: 1.5,
            });
            layer.add(line);
        }

        const label = new Konva.Text({
            x: margin * 3,
            y: y - 6,
            text: i.toFixed(1),
            fontSize: 10,
            fontFamily: 'Arial',
            fill: 'black',
        });
        layer.add(label);
    }

    if (tt !== 'NE' && tt !== 'NO') {
        if (alData.alValue) {
            const layerStartY = barY + (alData.alValue / maxDepth) * barHeight - 20;
            const nextDepth = measurements[0].depth;
            const layerEndY = barY + (nextDepth / maxDepth) * barHeight - 20;
            const soilType = measurements[0].soilType || '';
            const layerHeight = layerEndY - layerStartY;
            if (soilType !== '') {
                currentSoil.value = getSoilLayerColor(soilType);
            }
            const layer_rect = new Konva.Rect({
                x: barX + 1,
                y: layerStartY,
                width: barWidth - 2,
                height: layerHeight,
                fill: currentSoil.value,
                stroke: currentSoil.value,
                strokeWidth: 0.5,
            });
            //layer.add(layer_rect);
        }
    }

    // Draw soil layers based on measurements
    if (tt) {
        // For undisturbed sampling with collision detection
        const labels = [];
        const minSpacing = 15; // Minimum pixels between labels
        let previousSoilType = '';

        // Collect all labels first
        for (let i = 0; i < measurements.length; i++) {
            const measurement = measurements[i];
            let soilType = measurement.soilType || '';

            if (soilType === '' && measurement.extraData) {
                for (const extra of measurement.extraData) {
                    if (extra.type === 'EM') {
                        soilType = extra.data;
                        break;
                    }
                }
            }

            if (soilType !== '' && soilType !== previousSoilType) {
                const originalY = barY + (measurement.depth / maxDepth) * barHeight - 20;
                labels.push({
                    soilType: soilType,
                    originalY: originalY,
                    adjustedY: originalY,
                    depth: measurement.depth,
                });
                previousSoilType = soilType;
            }
        }

        // Sort by original Y position
        labels.sort((a, b) => a.originalY - b.originalY);

        // Adjust positions to avoid overlap
        for (let i = 1; i < labels.length; i++) {
            const current = labels[i];
            const previous = labels[i - 1];

            if (current.adjustedY - previous.adjustedY < minSpacing) {
                current.adjustedY = previous.adjustedY + minSpacing;
            }
        }

        // Draw the adjusted labels
        for (const label of labels) {
            const soilTypeText = new Konva.Text({
                x: barX + barWidth + margin,
                y: label.adjustedY - margin / 3,
                text: `${label.soilType} ${label.depth}m`,
                fontSize: 8,
                fontFamily: 'Arial',
                fill: 'black',
            });
            layer.add(soilTypeText);

            // Draw line from original position to label
            const line = new Konva.Line({
                points: [barX + barWidth, label.originalY, barX + barWidth + margin, label.adjustedY],
                stroke: 'gray',
                strokeWidth: 0.5,
            });
            layer.add(line);
        }
        if (alData.alSoilType !== '') {
            console.log('HELLO');
            const soilTypeText = new Konva.Text({
                x: barX + barWidth + margin,
                y: barY + (alData.alValue / maxDepth) * barHeight - 20 - margin / 3,
                text: `${alData.alSoilType} ${alData.alValue} m`,
                fontSize: 8,
                fontFamily: 'Arial',
                fill: 'black',
            });
            layer.add(soilTypeText);
        }
    } else {
        // NOTE: this is disabled for now, coloring soil layers might not be needed
        // For other measurement types, draw colored soil layers
        for (let i = 0; i < measurements.length - 1; i++) {
            const currentDepth = measurements[i].depth;
            const nextDepth = measurements[i + 1].depth;
            const soilType = measurements[i + 1].soilType || '';

            const layerStartY = barY + (currentDepth / maxDepth) * barHeight - 20;
            const layerEndY = barY + (nextDepth / maxDepth) * barHeight - 20;
            const layerHeight = layerEndY - layerStartY;
            if (soilType !== '') {
                const color = getSoilLayerColor(soilType);
                if (color !== '') {
                    currentSoil.value = color;
                }
            }
            // Draw soil layer
            const layer_rect = new Konva.Rect({
                x: barX + 1,
                y: layerStartY,
                width: barWidth - 2,
                height: layerHeight,
                fill: currentSoil.value,
                stroke: currentSoil.value,
                strokeWidth: 0.5,
            });
            layer.add(layer_rect);
        }
    }

    if (measurements.length > 0) {
        const lastMeasurementDepth = measurements[measurements.length - 1].depth;
        const lastLayerY = barY + (lastMeasurementDepth / maxDepth) * barHeight - 20;

        // Draw end line
        const options = tt === 'PA' ? { dash: [5, 5] } : {};
        const endLine = new Konva.Line({
            points: [endLinePosition1, lastLayerY, endLinePosition2, lastLayerY],
            stroke: 'black',
            strokeWidth: 1.5,
            ...options,
        });
        layer.add(endLine);

        // Draw 'L' label for Painokairaus
        if (tt === 'PA') {
            const lLabel = new Konva.Text({
                x: endLinePosition2 + margin / 3,
                y: lastLayerY - margin / 2,
                text: 'L',
                fontSize: 8,
                fontFamily: 'Arial',
                fill: 'black',
            });
            layer.add(lLabel);
        }

        // Draw max depth label
        const maxDepthLabel = new Konva.Text({
            x: barX + barWidth + maxRightLength * stepSize + 10,
            y: lastLayerY + margin,
            text: maxDepth.toFixed(2) + ' (m)',
            fontSize: 8,
            fontFamily: 'Arial',
            fill: 'black',
        });
        layer.add(maxDepthLabel);
    }

    const startLevel = new Konva.Text({
        x: barX + margin,
        y: barY - margin * 3.3,
        text: surface,
        fontSize: 12,
        fontFamily: 'Arial',
        fill: 'black',
        fontStyle: 'bold',
    });
    layer.add(startLevel);
    currentSoil.value = '';

    // Draw RK icons after the depth bar is complete
    drawRKIcons(layer, stage, measurements, startY, tt);
};

export const drawHMLabels = (
    layer: Konva.Layer,
    stage: Konva.Stage,
    hmMeasurements: Measurement[],
    filteredMeasurements: Measurement[],
    startY: number,
    tt: string,
    HM: string
) => {
    const { barX, barY, barWidth, maxDepth, barHeight, maxRightLength, stepSize, margin } = calculateValues(tt, stage, startY, filteredMeasurements);
    const adjustedBarY = barY - 20;
    for (const hmMeasurement of hmMeasurements) {
        const depth = hmMeasurement.depth;
        const hmText = hmMeasurement.hmText || '';
        const y = adjustedBarY + (depth / maxDepth) * barHeight;
        const tempText = new Konva.Text({
            text: hmText,
            fontSize: 8,
            fontFamily: 'Arial',
        });

        const textWidth = tempText.getTextWidth();
        const textHeight = tempText.height();

        // Position after the right x-axis with a small margin
        const maxRight = maxRightLength == 0 ? margin * 5 : maxRightLength;
        const rightAxisEnd = barX + barWidth + maxRight * (maxRightLength == 0 ? 1 : stepSize);
        const baseTextX = rightAxisEnd + margin;

        // Calculate available space to the right edge of the stage
        const availableWidth = stage.width() - baseTextX;

        // Adjust X position based on text width and available space
        let adjustedTextX = baseTextX;

        if (textWidth > availableWidth) {
            // If text is too wide, align it to the right edge of the stage
            adjustedTextX = stage.width() - textWidth - 5; // 5px margin from edge

            // Ensure it doesn't go beyond the left side of the right axis
            const minX = rightAxisEnd + 5;
            adjustedTextX = Math.max(adjustedTextX, minX);
        } else if (textWidth > availableWidth * 0.7) {
            // If text takes up more than 70% of available space, move it slightly left
            const offset = (textWidth - availableWidth * 0.7) * 0.5;
            adjustedTextX = baseTextX - offset;

            // Ensure it doesn't go beyond the left side of the right axis
            const minX = rightAxisEnd + 5;
            adjustedTextX = Math.max(adjustedTextX, minX);
        }

        // Ensure the text doesn't go outside the stage boundaries
        adjustedTextX = Math.max(5, Math.min(adjustedTextX, stage.width() - textWidth - 5));

        // Create the HM label
        if (depth == 0) {
            const label = new Konva.Text({
                x: 0,
                y: barY + barHeight + margin * 7,
                text: hmText,
                fontSize: 15,
                fontFamily: 'Arial',
                fill: 'black',
                width: stage.width(),
                align: 'center',
            });
            layer.add(label);
        } else {
            // Create a temporary text object to measure text width
            const label = new Konva.Text({
                x: adjustedTextX,
                y: y - textHeight / 2, // Center vertically on the depth line
                text: hmText,
                fontSize: 8,
                fontFamily: 'Arial',
                fill: 'black',
            });

            layer.add(label);
        }
    }

    if (HM !== '') {
        // For the main HM text, also consider its width for better centering
        const tempText = new Konva.Text({
            text: HM,
            fontSize: 15,
            fontFamily: 'Arial',
        });

        const textWidth = tempText.getTextWidth();
        const stageWidth = stage.width();

        // If text is too wide for the stage, reduce font size or break into lines
        let fontSize = 15;
        let finalText = HM;

        if (textWidth > stageWidth - 20) {
            // 20px total margin
            // Try smaller font first
            fontSize = 12;
            tempText.fontSize(fontSize);

            if (tempText.getTextWidth() > stageWidth - 20) {
                // If still too wide, break into multiple lines
                const words = HM.split(' ');
                const lines = [];
                let currentLine = '';

                for (const word of words) {
                    const testLine = currentLine + (currentLine ? ' ' : '') + word;
                    tempText.text(testLine);

                    if (tempText.getTextWidth() > stageWidth - 20 && currentLine) {
                        lines.push(currentLine);
                        currentLine = word;
                    } else {
                        currentLine = testLine;
                    }
                }

                if (currentLine) {
                    lines.push(currentLine);
                }

                finalText = lines.join('\n');
            }
        }

        const label = new Konva.Text({
            x: 0,
            y: barY + barHeight + margin * 8,
            text: finalText,
            fontSize: fontSize,
            fontFamily: 'Arial',
            fill: 'black',
            width: stage.width(),
            align: 'center',
        });

        layer.add(label);
    }
};

export const drawCUValues = (layer: Konva.Layer, stage: Konva.Stage, measurements: Measurement[], startY: number, alValue: number) => {
    const { stepSize, barHeight, maxDepth, barWidth, maxLeftLength, barX, barY, rightStepSize, leftStepSize } = calculateValues('CU', stage, startY, measurements);

    const adjustedBarY = barY - 20;

    if (measurements.length > 1) {
        if (maxLeftLength > 0 && measurements.some(m => m.value4 !== undefined && m.value4 !== null)) {
            const leftPoints = [];
            measurements.forEach(m => {
                const y = adjustedBarY + (m.depth / maxDepth) * barHeight;
                const porePressure = m.value4 || 0;
                const x = barX - (Math.abs(porePressure) / leftStepSize) * stepSize;
                leftPoints.push(x, y);
            });

            const leftLine = new Konva.Line({
                points: leftPoints,
                stroke: 'black',
                strokeWidth: 1.5,
                tension: 0.1,
            });
            layer.add(leftLine);
        }

        const rightValueConfigs = [
            { key: 'value1' as keyof Measurement, paramIndex: 0 },
            { key: 'value2' as keyof Measurement, paramIndex: 1 },
            { key: 'value3' as keyof Measurement, paramIndex: 2 },
        ];

        rightValueConfigs.forEach(config => {
            if (measurements.some(m => m[config.key] !== undefined && m[config.key] !== null)) {
                const rightPoints = [];
                measurements.forEach(m => {
                    const y = adjustedBarY + (m.depth / maxDepth) * barHeight;
                    const value = (m[config.key] as number) || 0;
                    const x = barX + barWidth + (value / rightStepSize) * stepSize;
                    rightPoints.push(x, y);
                });

                const lineOptions: Konva.LineConfig = {
                    points: rightPoints,
                    stroke: 'black',
                    strokeWidth: 1.5,
                    tension: 0.1,
                };

                // Use automatic dash pattern assignment
                const dashIndex = config.paramIndex % DASH_PATTERNS_ARRAY.length;
                const dashPattern = DASH_PATTERNS_ARRAY[dashIndex];
                if (dashPattern) {
                    lineOptions.dash = dashPattern;
                }

                const line = new Konva.Line(lineOptions);
                layer.add(line);
            }
        });
    }
};

export const drawPOValues = (layer: Konva.Layer, stage: Konva.Stage, measurements: Measurement[], startY: number, alValue: number) => {
    const { stepSize, barX, barY, maxDepth, barHeight, leftStepSize } = calculateValues('PO', stage, startY, measurements);

    // Account for the -20 offset used in drawDepthBar
    const adjustedBarY = barY - 20;

    // Draw the measurement lines using the calculated step size
    for (let i = 0; i < measurements.length - 1; i++) {
        const curr = measurements[i];
        const next = measurements[i + 1];

        const depth1 = curr.depth;
        const depth2 = next.depth;
        const value1 = curr.value1;
        const value2 = next.value1;

        // Calculate positions - drawing to the left of the bar using leftStepSize
        const x1 = barX - (value1 / leftStepSize) * stepSize;
        const y1 = adjustedBarY + (depth1 / maxDepth) * barHeight;
        const x2 = barX - (value2 / leftStepSize) * stepSize;
        const y2 = adjustedBarY + (depth2 / maxDepth) * barHeight;
        if (i == 0) {
            // Draw the first vertical line from zero x to first value
            drawLine(layer, barX, x1, y1, y1);
        }
        drawBars(layer, x1, x2, y1, y2);

        // Draw the first vertical line from al depth if this is the first measurement
        if (i === 0 && alValue) {
            const firstLine = new Konva.Line({
                points: [x1, adjustedBarY + (alValue / maxDepth) * barHeight, x1, y1],
                stroke: 'black',
                strokeWidth: 1.0,
            });
            layer.add(firstLine);
        }
    }
    // Draw the final horizontal line at the last depth using leftStepSize
    if (measurements.length > 0) {
        const lastMeasurement = measurements[measurements.length - 1];
        const lastX = barX - (lastMeasurement.value1 / leftStepSize) * stepSize;
        const lastY = adjustedBarY + (lastMeasurement.depth / maxDepth) * barHeight;

        // Extend the line to the edge of the bar
        const finalLine = new Konva.Line({
            points: [lastX, lastY, barX, lastY],
            stroke: 'black',
            strokeWidth: 1.0,
        });
        layer.add(finalLine);
    }
};

export const drawNONEValues = (layer: Konva.Layer, stage: Konva.Stage, measurements: Measurement[], startY: number) => {
    const { stepSize, barHeight, barWidth, barY, barX, maxDepth, maxLeftLength, maxRightLength, leftStepSize, rightStepSize } = calculateValues('NE', stage, startY, measurements);

    // Account for the -20 offset used in drawDepthBar
    const adjustedBarY = barY - 20;

    // Get NONE values with left/right side assignments
    const { data } = getNONEValues(measurements);

    const drawSideLines = (sideData: { [param: string]: { depth: number; value: number }[] }, side: 'left' | 'right', sideStepSize: number) => {
        const paramNames = Object.keys(sideData);

        paramNames.forEach((param, paramIndex) => {
            const paramData = sideData[param];

            // Draw lines connecting consecutive points for this parameter
            for (let i = 0; i < paramData.length - 1; i++) {
                const curr = paramData[i];
                const next = paramData[i + 1];

                const currY = adjustedBarY + (curr.depth / maxDepth) * barHeight;
                const nextY = adjustedBarY + (next.depth / maxDepth) * barHeight;

                let currX: number, nextX: number;

                if (side === 'left') {
                    currX = barX - (curr.value / sideStepSize) * stepSize;
                    nextX = barX - (next.value / sideStepSize) * stepSize;
                } else {
                    // right side
                    currX = barX + barWidth + (curr.value / sideStepSize) * stepSize;
                    nextX = barX + barWidth + (next.value / sideStepSize) * stepSize;
                }

                const lineOptions: Konva.LineConfig = {
                    points: [currX, currY, nextX, nextY],
                    stroke: 'black',
                    strokeWidth: 1.5,
                };

                // Use different dash pattern for each parameter (cycle through patterns)
                const dashIndex = paramIndex % DASH_PATTERNS_ARRAY.length;
                const dashPattern = DASH_PATTERNS_ARRAY[dashIndex];
                if (dashPattern) {
                    lineOptions.dash = dashPattern;
                }

                const line = new Konva.Line(lineOptions);
                layer.add(line);
            }
        });
    };

    // Draw lines for left side parameters
    if (maxLeftLength > 0) {
        drawSideLines(data.left, 'left', leftStepSize);
    }

    // Draw lines for right side parameters
    if (maxRightLength > 0) {
        drawSideLines(data.right, 'right', rightStepSize);
    }
};

export const drawHEValues = (layer: Konva.Layer, stage: Konva.Stage, measurements: Measurement[], startY: number, alValue: number) => {
    const { stepSize, barHeight, maxDepth, barWidth, barX, barY, rightStepSize } = calculateValues('HE', stage, startY, measurements);

    // Account for the -20 offset used in drawDepthBar
    const adjustedBarY = barY - 20;
    const dashIndex = 1 % DASH_PATTERNS_ARRAY.length;
    const dashPattern = DASH_PATTERNS_ARRAY[dashIndex];
    const lineOptions: Partial<Konva.LineConfig> = {};
    if (dashPattern) {
        lineOptions.dash = dashPattern;
    }
    for (let i = 0; i < measurements.length - 1; i++) {
        const curr = measurements[i];
        const next = measurements[i + 1];
        const depth1 = curr.depth;
        const depth2 = next.depth;
        const value1 = curr.value1;
        const value2 = next.value1;
        const y1 = adjustedBarY + (depth1 / maxDepth) * barHeight;
        const y2 = adjustedBarY + (depth2 / maxDepth) * barHeight;
        const x1 = barX + barWidth + (value1 / rightStepSize) * stepSize;
        const x2 = barX + barWidth + (value2 / rightStepSize) * stepSize;

        // Draw horizontal and vertical lines
        drawBars(layer, x1, x2, y1, y2, lineOptions);
        if (i + 1 == measurements.length - 1) {
            // Draw last line to zero
            const x_zero = barX + barWidth + (0 / rightStepSize) * stepSize;
            drawLine(layer, x2, x_zero, y2, y2, lineOptions);
        }
    }
};

export const drawHPValues = (layer: Konva.Layer, stage: Konva.Stage, measurements: Measurement[], startY: number, alValue: number) => {
    const { stepSize, barHeight, maxDepth, barWidth, barX, barY, leftStepSize, rightStepSize } = calculateValues('HP', stage, startY, measurements);

    // Account for the -20 offset used in drawDepthBar
    const adjustedBarY = barY - 20;

    const dashIndex = 1 % DASH_PATTERNS_ARRAY.length;
    const dashPattern = DASH_PATTERNS_ARRAY[dashIndex];
    const lineOptions: Partial<Konva.LineConfig> = {};
    if (dashPattern) {
        lineOptions.dash = dashPattern;
    }

    for (let i = 0; i < measurements.length - 1; i++) {
        const curr = measurements[i];
        const next = measurements[i + 1];

        const depth1 = curr.depth;
        const depth2 = next.depth;
        const value2_1 = curr.value2;
        const value2_2 = next.value2;
        const value1_1 = curr.value1;
        const value1_2 = next.value1;
        const y1 = adjustedBarY + (depth1 / maxDepth) * barHeight;
        const y2 = adjustedBarY + (depth2 / maxDepth) * barHeight;
        const x1_value2 = barX - (value2_1 / leftStepSize) * stepSize;
        const x2_value2 = barX - (value2_2 / leftStepSize) * stepSize;
        // Draw to the right side
        const x1_value1 = barX + barWidth + (value1_1 / rightStepSize) * stepSize;
        const x2_value1 = barX + barWidth + (value1_2 / rightStepSize) * stepSize;

        // Draw horizontal and vertical lines
        drawBars(layer, x1_value2, x2_value2, y1, y2);
        if (i + 1 == measurements.length - 1) {
            // Draw last line to zero
            const x_zero = barX + barWidth + (0 / rightStepSize) * stepSize;
            drawLine(layer, x2_value1, x_zero, y2, y2);
        }
        const hasHType = curr.extraData && curr.extraData.some(e => e.type === 'HP' && e.data === 'H');
        if (hasHType) {
            drawBars(layer, x1_value1, x2_value1, y1, y2);
            if (i + 1 == measurements.length - 1) {
                // Draw last line to zero
                const x_zero = barX + barWidth + (0 / rightStepSize) * stepSize;
                drawLine(layer, x2_value1, x_zero, y2, y2);
            }
        } else {
            drawLine(layer, x1_value1, x2_value1, y1, y2, lineOptions);
        }
    }
};

export const drawSIValues = (layer: Konva.Layer, stage: Konva.Stage, measurements: Measurement[], startY: number) => {
    const { stepSize, barHeight, barWidth, barY, barX, maxDepth, rightStepSize } = calculateValues('SI', stage, startY, measurements);

    // Account for the -20 offset used in drawDepthBar
    const adjustedBarY = barY - 20;

    for (let i = 0; i < measurements.length - 1; i++) {
        const curr = measurements[i];
        const next = measurements[i + 1];

        const currY = adjustedBarY + (curr.depth / maxDepth) * barHeight;
        const nextY = adjustedBarY + (next.depth / maxDepth) * barHeight;

        // Define value configurations
        const valueConfigs = [
            { key: 'value1' as keyof Measurement, dashIndex: 0 }, // solid
            { key: 'value2' as keyof Measurement, dashIndex: 1 }, // dashed
            { key: 'value3' as keyof Measurement, dashIndex: 2 }, // dotted
            { key: 'value4' as keyof Measurement, dashIndex: 3 }, // dash-dot
        ];

        // Draw lines for all values
        valueConfigs.forEach(config => {
            const currValue = curr[config.key] as number;
            const nextValue = next[config.key] as number;

            if (currValue !== null && currValue !== undefined && nextValue !== null && nextValue !== undefined) {
                const currX = barX + barWidth + (currValue / rightStepSize) * stepSize;
                const nextX = barX + barWidth + (nextValue / rightStepSize) * stepSize;

                const lineOptions: Konva.LineConfig = {
                    points: [currX, currY, nextX, nextY],
                    stroke: 'black',
                    strokeWidth: 1.5,
                };

                // Add dash pattern if not solid
                const dashPattern = DASH_PATTERNS_ARRAY[config.dashIndex];
                if (dashPattern) {
                    lineOptions.dash = dashPattern;
                }

                const line = new Konva.Line(lineOptions);
                layer.add(line);
            }
        });
    }
};

export const drawPAValues = (layer: Konva.Layer, stage: Konva.Stage, measurements: Measurement[], startY: number, alValue: number) => {
    const { stepSize, barHeight, maxDepth, barWidth, maxRightLength, maxLeftLength, barX, barY, rightStepSize } = calculateValues('PA', stage, startY, measurements);

    // Account for the -20 offset used in drawDepthBar
    const adjustedBarY = barY - 20;
    const offset = 10;
    let lineBreak = false;

    // Draw the measurement lines
    for (let i = 0; i < measurements.length - 1; i++) {
        const curr = measurements[i];
        const next = measurements[i + 1];

        const depth1 = curr.depth;
        const depth2 = next.depth;
        const value1 = curr.value2; // Right side values
        const value2 = next.value2;

        const y1 = adjustedBarY + (depth1 / maxDepth) * barHeight;
        const y2 = adjustedBarY + (depth2 / maxDepth) * barHeight;

        let x1: number, x2: number;

        if (value1 < 0) {
            // Handle negative values (draw dashed line across)
            const line = new Konva.Line({
                points: [barX + barWidth, y1, barX + barWidth + maxRightLength * stepSize, y1],
                stroke: 'black',
                strokeWidth: 1.0,
                dash: [5, 5],
            });
            layer.add(line);

            const lLabel = new Konva.Text({
                x: barX + barWidth + maxRightLength * stepSize + 5,
                y: y1 - offset / 2,
                text: 'L',
                fontSize: 8,
                fontFamily: 'Arial',
                fill: 'black',
            });
            layer.add(lLabel);
            lineBreak = true;
            continue;
        }

        if (value1 === 0) {
            // Left side value
            x1 = barX - ((curr.value1 * 9.81) / 1000 / maxLeftLength) * maxLeftLength * stepSize;
        } else {
            // Right side value using rightStepSize
            x1 = barX + barWidth + (Math.max(0, value1) / rightStepSize) * stepSize;
        }

        if (value2 === 0) {
            // Left side value
            x2 = barX - ((next.value1 * 9.81) / 1000 / maxLeftLength) * maxLeftLength * stepSize;
        } else {
            // Right side value using rightStepSize
            x2 = barX + barWidth + (Math.max(0, value2) / rightStepSize) * stepSize;
        }

        if (lineBreak) {
            x1 = barX + barWidth;
            lineBreak = false;
        }

        // Draw horizontal and vertical lines
        drawBars(layer, x1, x2, y1, y2);

        // Draw the first horizontal line from al depth if this is the first measurement
        if (i === 0 && alValue) {
            const zeroX = barX + (curr.value1 != 0 ? barWidth : 0) + (0 / maxDepth) * barHeight;
            const firstLine = new Konva.Line({
                points: [zeroX, y1, x1, y1],
                stroke: 'black',
                strokeWidth: 1.0,
            });
            layer.add(firstLine);
        }
    }
    // Draw a white rectangle over the bar area to cover any lines that cross through
    const coverRect = new Konva.Rect({
        x: barX,
        y: adjustedBarY,
        width: barWidth,
        height: barHeight,
        fill: 'white',
        stroke: 'black',
        strokeWidth: 1.0,
    });
    layer.add(coverRect);
};
